// Drawer «Editar mantención» (handoff §6): Nombre, Cada + Unidad; pie
// con «Eliminar» (danger, a la izquierda) · Cancelar · Guardar. Guardar
// queda deshabilitado hasta que haya un cambio válido y envía sólo los
// campos que cambiaron (PATCH parcial).
//
// Sin «Proveedor sugerido» ni «Avisar antes»: maintenance_items no tiene
// esas columnas todavía (plan §2.7) y esta ronda no migra; se ocultan
// (regla «hide unusable actions»), no se simulan.
//
// «Eliminar» es PATCH active=false — el historial de realizaciones se
// conserva, como pide el handoff — tras una confirmación: el catálogo no
// tiene una ruta para reactivar desde la UI, así que un clic de más no
// puede decidir solo.

import { useId, useState } from "react";
import type {
  MaintenanceItemAdminSummary,
  MaintenanceItemUpdateBody,
} from "@bitacorapro/api-types";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { Drawer } from "../../../components/Drawer";
import { ApiError } from "../../../lib/api/error";
import { CadenceFields } from "./CadenceFields";
import { composeCadence, decomposeCadence, isValidEvery, type CadenceUnit } from "./cadence";
import { usePatchMaintenanceItem } from "./queries";
import type { MantencionWords } from "./words";

export type EditarMantencionDrawerProps = {
  item: MaintenanceItemAdminSummary;
  words: MantencionWords;
  onClose: () => void;
  /** Mensaje del toast que muestra la pantalla una vez cerrado el drawer. */
  onSaved: (message: string) => void;
};

export function EditarMantencionDrawer({
  item,
  words,
  onClose,
  onSaved,
}: EditarMantencionDrawerProps) {
  const initial = decomposeCadence(item.cadenceDays);
  const [label, setLabel] = useState(item.label);
  const [every, setEvery] = useState(String(initial.every));
  const [unit, setUnit] = useState<CadenceUnit>(initial.unit);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const patch = usePatchMaintenanceItem();
  const nameId = useId();
  const errorId = useId();

  const trimmedLabel = label.trim();
  const labelChanged = trimmedLabel.length > 0 && trimmedLabel !== item.label;
  const cadenceValid = isValidEvery(every);
  const nextCadenceDays = cadenceValid ? composeCadence({ every: Number(every), unit }) : null;
  const cadenceChanged = nextCadenceDays !== null && nextCadenceDays !== item.cadenceDays;
  const canSave =
    !patch.isPending && trimmedLabel.length > 0 && cadenceValid && (labelChanged || cadenceChanged);

  async function save() {
    setError(null);
    const body: MaintenanceItemUpdateBody = {
      ...(labelChanged ? { label: trimmedLabel } : {}),
      ...(cadenceChanged && nextCadenceDays !== null ? { cadenceDays: nextCadenceDays } : {}),
    };
    try {
      await patch.mutateAsync({ itemId: item.id, body });
      onSaved("Mantención actualizada · aplica desde la próxima fecha");
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "MAINTENANCE_ITEM_NOT_FOUND"
          ? "Esta mantención ya no existe. Cierra el panel y vuelve a cargar la lista."
          : "No pudimos guardar los cambios. Intenta de nuevo."
      );
    }
  }

  async function remove() {
    setConfirmDelete(false);
    setError(null);
    try {
      await patch.mutateAsync({ itemId: item.id, body: { active: false } });
      onSaved(`${item.label} eliminada · el historial de realizaciones se conserva`);
      onClose();
    } catch {
      setError("No pudimos eliminar la mantención. Intenta de nuevo.");
    }
  }

  return (
    <>
      <Drawer
        title={`Editar mantención · ${item.label}`}
        onClose={onClose}
        footerStart={
          <button
            type="button"
            className="btn btn--danger"
            title={`Dejar de programar esta mantención en todas las ${words.assetPluralLower}; el historial se conserva`}
            disabled={patch.isPending}
            onClick={() => setConfirmDelete(true)}
          >
            Eliminar
          </button>
        }
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              title="Cerrar sin guardar cambios"
              disabled={patch.isPending}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              title="Guardar el nombre y la frecuencia de esta mantención"
              disabled={!canSave}
              onClick={() => void save()}
            >
              {patch.isPending ? "Guardando…" : "Guardar"}
            </button>
          </>
        }
      >
        <p className="ag-muted ag-mb16">
          Cambia cómo se programa esta mantención. Las realizaciones ya marcadas no se modifican.
        </p>
        {error && (
          <p id={errorId} className="auth-error" role="alert">
            {error}
          </p>
        )}
        <div className="field ag-field">
          <label className="field__label" htmlFor={nameId}>
            Nombre
          </label>
          <input
            id={nameId}
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={patch.isPending}
            maxLength={200}
            aria-describedby={error ? errorId : undefined}
          />
        </div>
        <CadenceFields
          every={every}
          unit={unit}
          onEveryChange={setEvery}
          onUnitChange={setUnit}
          disabled={patch.isPending}
        />
      </Drawer>
      <ConfirmDialog
        open={confirmDelete}
        title={`¿Eliminar «${item.label}»?`}
        description={`Deja de programarse en todas las ${words.assetPluralLower}. Las realizaciones ya registradas se conservan en el historial de cada ${words.assetLower}.`}
        confirmLabel="Eliminar mantención"
        onConfirm={() => void remove()}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
